import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { environmentSelector } from '../../selectors/commonSelectors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import useIssuerEdiTesting from './useIssuerEdiTesting';
import {
  itemStatus,
  insuranceType,
  stepPositions,
  paramOptions,
  allowedRoles
} from './IssuerConstants';
import { parseDate } from '../../utils/dates/date.utils';

// components

import { GIButton, Table } from 'app-components';
import AccordionPanel from '../../components/common/app-components/AccordionPanel';

// small helper function to to convert Started from backend to front end In Progress
const convertItemStatus = function (status) {
  if (status === 'Started') {
    return 'In Progress';
  }
  return status
}

const columns = [
  {
    titleId: 'Scenario',
    key: 'scenarioId',
    render: (item: any) => {
      return (
        <>
          <p className='scenario-cell'>{item.scenarioId}</p>
        </>
      )
    }
  },
  {
    titleId: 'Title',
    key: 'title'
  },
  {
    titleId: 'Started',
    key: 'startDate',
    render: (item: any) => {
      const time = parseDate(item.startDate)
      return (
        (time.isValid() ? 
        <>
          <div>{time.format('MM/DD/YYYY')}</div> {time.format('h:mm A')}
        </>  
        : '-')
      )
    }
  },
  {
    titleId: 'Ended',
    key: 'endDate',
    render: (item: any) => {
      const time = parseDate(item.endDate)
      return (
        (time.isValid() ? 
        <>
          <div>{time.format('MM/DD/YYYY')}</div> {time.format('h:mm A')}
        </>  
        : '-')
      )
    }
  },
  {
    titleId: 'Status',
    key: 'status',
    render: (item: any) => {
      return (
        <div className={item.status === itemStatus.IN_PROGRESS ? "display-flex active-scenario-row" : "display-flex"} >
          <div className="step-wrapper">
              {(item.position === stepPositions.FIRST || item.position === stepPositions.ONLY) ? '' : <div className="vertical-line-top"></div>}
              {(item.status === itemStatus. NOT_STARTED) ? <div className="incomplete-circle" /> : <FontAwesomeIcon icon="circle" size="xs" className={item.status === itemStatus.IN_PROGRESS ? "active-circle" : "done-circle"} />}
              {(item.position === stepPositions.LAST || item.position === stepPositions.ONLY) ? '' : <div className="vertical-line"></div>}
          </div>
          <div className="table-status">{convertItemStatus(item.status)}</div>
        </div>
      )
    }
  }
];

function IssuerEdiTestingContainer() {

  const { id = null } = useParams<{ id: string }>(); // Use `useParams` to get the `id` parameter from the URL

    const {
      user
    } = useSelector(environmentSelector);

    const issuerName = sessionStorage.getItem('issuerName');

    const { descriptionData, testResults, getTestDetails, getTestResults, apiError } = useIssuerEdiTesting();
    const [pageState, setPageState] = useState(insuranceType.HEALTH);
    const [healthData, setHealthData] = useState<any>(null);
    const [dentalData, setDentalData] = useState<any>(null);
    const [householdId, setHouseholdId] = useState<string | null>(sessionStorage.getItem('householdId'));

    const navigate = useNavigate();
    useEffect(() => {
      if (allowedRoles.includes(user.role)) {
        getTestResults(id);
        setHouseholdId(id);
      } else {
        // if EnrollmentRep navigates to param.id other than their hhid or ParamsNotSet
        if (id != paramOptions.EMPTY && id != householdId) {
          confirm('Issuer Enrollment Rep is not allowed to view Issuer Testing for other HHIDs, click ok to be redirected to your assigned testing page');
          window.history.pushState({}, "", `${window.location.origin}/hix/issuer-edi-testing/${householdId}`);
        } else {
          getTestResults(householdId);
        }
      }
      getTestDetails();
    }, []);

    useEffect(() => {
      if (Object.keys(descriptionData).length !== 0 && Object.keys(testResults).length !== 0) {
        if (testResults[0]?.insuranceType === insuranceType.HEALTH) {
            setHealthData(descriptionData[0]);
            setDentalData(descriptionData[1]);
        } else if (testResults[0]?.insuranceType === insuranceType.DENTAL) {
            setPageState(insuranceType.DENTAL);
            setHealthData(null);
            setDentalData(descriptionData[1]);
        }
      } else if(Object.keys(descriptionData).length !== 0) {
        if (descriptionData[0]?.insuranceType === insuranceType.HEALTH) {
          setHealthData(descriptionData[0]);
          setDentalData(descriptionData[1]);
        } else if (descriptionData[0]?.insuranceType === insuranceType.DENTAL) {
            setPageState(insuranceType.DENTAL);
            setHealthData(null);
            setDentalData(descriptionData[1]);
        }
      }
  }, [descriptionData, testResults])

    const ItemHeader = ({ title, buttonTitle, testDetails, testResponse, expanded }) => {
        const onRoute = () => {
            // best way to pass response and description response
              // keep data avaliable back and forth
            let pathName;
            global.__ENV__.PRODUCTION ? pathName = `/hix/issuer-edi-testing/details/${householdId}` : pathName = `/issuer-edi-testing/details/${householdId}`; 
            navigate(pathName, { state: {
              description: testDetails,
              response: testResponse,
            }})
        }

        return (
          <div className={expanded ? 'styled-header hide-extra-details' : 'styled-header' }>
            <div>{title}</div>
            <GIButton id='go_description' labelId={buttonTitle} onClickHandler={onRoute}/>
          </div>
        )
    };

    const TestTitle = ({ overallTestStatus, testCase }) => {
      return(
      <div className="header-test">
          <div className="accordion-tab font-weight-bold accordion-id">{`Test Case ${parseInt(testCase.testCaseId)}`}</div>
          <div className="accordion-tab accordion-title">{testCase.testCaseTitle}</div>
          <div className='hideExtraDetails'>
            <div className="accordion-tab accordion-scenario">
            <p className="remove-margin-tb accordion-tab-header">Currently on:</p>
            <p className='remove-margin-tb accordion-scenario-title'>
            {testCase.activeScenario != null ? 
              <><span className='font-weight-bold'>Scenario {testCase.activeScenario + 1}</span> - {testCase.scenarios[testCase.activeScenario]?.scenarioTitle}</>
              : '-'}
            </p>
          </div>
          <div className="accordion-tab accordion-status">
            <p className="remove-margin-tb accordion-tab-header">Status:</p>
            <p className={overallTestStatus === itemStatus.IN_PROGRESS ? "in-progress-status  remove-margin-tb " : "remove-margin-tb not-started-status"}><FontAwesomeIcon icon="circle" size="xs" className="icon-link" /> {convertItemStatus(overallTestStatus)}</p>
          </div>
        </div>
      </div>)
    };

    // create my own Testdata object with properties avaliable, and indexes as expected : testCase
    const Tests = ({ sid, columns, testCase }) => {
      const [expanded, setExpanded] = useState(false);

      // create data
      // start with local Health or dental
      if (descriptionData[0]) {
        testCase.insuranceType = pageState;
        testCase.hiosIssuerId = householdId;
        let scenarios : any[] = [];
        let rowStatus: string = '';
        let overallTestStatus = itemStatus.NOT_STARTED;

        let res;
        let resultsIndex;
        if (testResults[0]?.insuranceType === pageState) {
          resultsIndex = 0;
        } else if (testResults[1]?.insuranceType === pageState) {
          resultsIndex = 1;
        }

        const results = testResults[resultsIndex]?.testCases;
        if (results) {
          res = results.find(test => sid === (parseInt(test.testCaseId)-1));
        }

        for (let i=0; i<testCase.scenarios.length; i++) {
          // find the "In Progress"
            // what if no in-progress?
          // loop through steps inside scenarios

          // check testResult for inProgressSteps
          
          let scenarioObj;
          if (res && res.scenarios[i]) {
            const inProgressSteps = res.scenarios[i].steps.filter(step => step.status === itemStatus.IN_PROGRESS);
            const doneSteps = res.scenarios[i].steps.filter(step => step.status === itemStatus.COMPLETED);

            // if completed. last scenario all steps inside scenario complete
            if (doneSteps.length === testCase.scenarios[i].steps.length && i === testCase.scenarios.length-1) {
              // show next scenario
              overallTestStatus = itemStatus.COMPLETED;
              testCase.activeStep = testCase.scenarios[i].steps.length;
            } else if (inProgressSteps.length > 0) {
              testCase.activeScenario = i;
              testCase.activeStep = parseInt(inProgressSteps[0].stepId);
              overallTestStatus = itemStatus.IN_PROGRESS;
              rowStatus = itemStatus.IN_PROGRESS;
            } else if (doneSteps.length > 0) {
              // not last scenario but all steps done inside scenario
              if (doneSteps.length === testCase.scenarios[i].steps.length) {
                testCase.activeScenario = i+1;
                testCase.activeStep = 1;
                rowStatus = itemStatus.COMPLETED;
              } else {
                testCase.activeScenario = i;
                testCase.activeStep = doneSteps.length+1;
                rowStatus = itemStatus.IN_PROGRESS;
              }
              overallTestStatus = itemStatus.IN_PROGRESS;
            } else {
              overallTestStatus = itemStatus.NOT_STARTED;
            }

            scenarioObj = {
              scenarioId: i+1,
              title: testCase.scenarios[i].scenarioTitle,
              startDate: res.scenarios[i].startTime,
              endDate: res.scenarios[i].endTime,
              status: rowStatus,
              position: ''
            }
            // if no step in progress?
            if (!testCase.activeStep) {
              testCase.activeScenario = null;
              testCase.activeStep = null;
            }
          } else {
            // done or not started, incomplete
            scenarioObj = {
              scenarioId: i+1,
              title: testCase.scenarios[i].scenarioTitle,
              startDate: '',
              endDate: '',
              status: itemStatus.NOT_STARTED,
              position: ''
            }
            if (overallTestStatus === itemStatus.NOT_STARTED) {
              testCase.activeScenario = null;
              testCase.activeStep = null;
            }
          }
          
          if (i === 0) {
            scenarioObj.position = stepPositions.FIRST
          } else if (i === testCase.scenarios.length-1) {
            scenarioObj.position = stepPositions.LAST;
          }
          if (testCase.scenarios.length === 1) {
            scenarioObj.position = stepPositions.ONLY;
          }
          scenarios.push(scenarioObj)
        }

        const onToggleCall = function (state) {
          setExpanded(!state);
        }

        return (
          <>
            <AccordionPanel id={sid} onToggle={onToggleCall} header={<ItemHeader expanded={expanded} title={<TestTitle overallTestStatus={overallTestStatus} testCase={testCase} />} buttonTitle="Select" testDetails={testCase} testResponse={testCase} />} >
                <Table columns={columns} data={scenarios} />
            </AccordionPanel>
          </>
        )
      } else {
        return (<></>)
      }
    };

    return (
        <main className="usa-layout-docs usa-section gi-main-section gi-main-section-edi" id="issuer-edi-testing">
            <div className="grid-container gi-grid-container issuer-edi-container">
                {!apiError &&
                <div className='grid-row'>
                  <h1 className="big-title">{issuerName}</h1>
                </div>}
                {apiError && 
                <div className="usa-alert usa-alert--warning gi-alert gi-alert__error">
                    <div className="usa-alert__body gi-alert__warning__body">
                        <p className="usa-alert__text gi-alert__warning__text">
                            {apiError}
                        </p>
                     </div>
                </div>}
                <div className='edi-content'>
                <div className='grid-row'>
                    <h4 className='styled-title'>Issuer EDI Testing</h4>
                </div>
                <div className="display-flex">
                  {healthData ?
                    <div onClick={() => setPageState(insuranceType.HEALTH)} className={pageState === insuranceType.HEALTH ? 'active-tab insurance-type' : 'insurance-type'}>
                      <FontAwesomeIcon icon="plus" size="xs" className="blue mr-10px" />
                      Health
                    </div>
                  : ''}
                  {dentalData ?
                    <div onClick={() => setPageState(insuranceType.DENTAL)} className={pageState === insuranceType.DENTAL ? 'active-tab insurance-type' : 'insurance-type'}>
                      <FontAwesomeIcon icon="tooth" size="xs" className="blue mr-10px" />
                      Dental</div>
                  : ''}
                </div> 
                {(pageState === insuranceType.HEALTH) && healthData ?
                healthData.testCases.map((test, sid) => (
                  <div key={sid} className='mt-2p'>
                    <Tests sid={sid} columns={columns} testCase={test}  />
                  </div>))
                : '' }
                {(pageState === insuranceType.DENTAL) && dentalData ?
                dentalData.testCases.map((test, id) => {
                  return (<div key={id} className='mt-2p'>
                    <Tests sid={id} columns={columns} testCase={test} />
                  </div>)})
                : ''}
                </div>
            </div>
        </main>
    );
}

export default IssuerEdiTestingContainer;